import React, { useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { get } from 'lodash';
import { TextArea } from '@quintype/em/components/text-area';
import { Radio, RadioOption } from '@quintype/em/components/radio-button';
import styles from './styles.module.css';
import {
  updateBackupConfig,
  updateConfigVersion,
  updateHaveUnsavedChanges,
  updateHistoryActionStatus,
  updatePBConfig,
  ampSetting
} from '../../actions/builder-action';
import { updateCustomCodeGeneralSettings, updateAmpWidgetSettings } from '../../actions/custom-code-action';
import { checkValidEncodedCustomCode, encodeCustomCode, saveConfig } from '../../utils/utils';
import { Button } from '@quintype/em/components/button';
import { IParams } from '../../utils/interfaces';
import { Close } from '../../atoms/close-svg';
import { ConfirmationPopup } from '../confirmation-popup/index';
import { validateCode } from './amp-validator';
import { CustomCallout } from './custom-callout';
import { DisplayMessage } from '../display-message';
import { Checkbox } from '@quintype/em/components/checkbox';
import { AmpWidget } from '../../utils/interfaces';

const AmpCustomCode = () => {
  const infiniteScrollExists = useSelector((state: any | {}) =>
    get(state, ['builder', 'config', 'general', 'amp', 'ampValidatorInfiniteScrollExists'], true)
  );
  const [currentTab, setCurrentTab] = useState<string | number>('ampGenericStoryPage');
  const [showConfirmationPopup, toggleConfirmationPopup] = useState(false);
  const [validationPopup, setValidationPopup] = useState(false);
  const [validationStatus, setValidationStatus] = useState();
  const [errorMessage, setErrorMessage] = useState('');
  const [testedHtmlStr, setTestedHtmlStr] = useState('');
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [showSaveMsg, setShowSaveMsg] = useState(false);
  const [showErrMsg, setShowErrMsg] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const { publisherId = '', domain } = useParams<IParams>();
  const { insertToHead = '', insertToBody = '' } = useSelector((state: any) =>
    get(state, ['builder', 'config', 'general', 'tags', currentTab], {})
  );
  const widgets: AmpWidget[] = useSelector((state: any) =>
    get(
      state,
      ['builder', 'config', 'story', 'ampWidgets'],
      Array.from({ length: 6 }).fill({ code: '', enable: false })
    )
  );

  const { config, haveUnsavedChanges, backupConfig, editControl } = useSelector(state => get(state, ['builder']));
  const hasEditAccess = get(editControl, ['access'], false);

  const updateSettings = (key: string, value: string, currentValue: string) => {
    dispatch(
      updateCustomCodeGeneralSettings({
        [currentTab]: {
          ...{ insertToHead, insertToBody },
          ...{ [key]: encodeCustomCode(value) }
        }
      })
    );

    dispatch(updateHaveUnsavedChanges(true));
  };

  const infiniteScrollRadioBtnHandler = (e: any) => {
    const infScrExists = e.target.value === 'true';
    dispatch(ampSetting({ ampValidatorInfiniteScrollExists: infScrExists }));
    dispatch(updateHaveUnsavedChanges(true));
  };

  const navigateToHome = () => {
    history.push(`/accounts/${publisherId}/${domain}`);
  };

  const onActionButtonClick = async (navigate: boolean = true) => {
    const updatedConfig = { ...config, version: config.version + 1 };
    const response = await saveConfig(publisherId, updatedConfig, domain);
    if (response?.activeVersion && response?.actionButtonStatus) {
      handleSaveMsg();
      const { activeVersion = 0, actionButtonStatus } = response;
      batch(() => {
        dispatch(updateConfigVersion(activeVersion));
        dispatch(updateHistoryActionStatus(actionButtonStatus));
        dispatch(updateBackupConfig({ config }));
        dispatch(updateHaveUnsavedChanges(false));
      });
    } else {
      setShowErrMsg(true);
      setTimeout(() => setShowErrMsg(false), 1500);
    }
    if (navigate) {
      navigateToHome();
    }
  };

  const onCancel = () => toggleConfirmationPopup(false);

  const onDiscard = () => {
    dispatch(updatePBConfig(backupConfig));
    toggleConfirmationPopup(false);
    dispatch(updateHaveUnsavedChanges(false));
    navigateToHome();
  };

  const onClose = () => {
    if (haveUnsavedChanges) {
      toggleConfirmationPopup(true);
    } else {
      navigateToHome();
    }
  };

  const insertCode = (label: string, tag: string, value: string, key: string) => (
    <TextArea
      label={label}
      placeholder={`Paste any valid AMP HTML code here. The code will be inserted to the ${tag}`}
      value={checkValidEncodedCustomCode(value)}
      onChange={(data: string) => updateSettings(key, data, String(currentTab))}
      disabled={!hasEditAccess}
    />
  );

  const handleSaveMsg = () => {
    setShowSaveMsg(true);
    setTimeout(() => {
      setShowSaveMsg(false);
    }, 1500);
  };

  const confirmationPopupSaveHandler = () => {
    onActionButtonClick();
  };

  const saveHandler = () => {
    onActionButtonClick(false);
    setValidationPopup(false);
  };

  const validateHandler = async () => {
    try {
      setErrorMessage('');
      setTestedHtmlStr('');
      setWaitingForResponse(true);
      const { statusTxt, errorMsg = '', testedHtml = '' } = await validateCode({
        insertToHead,
        insertToBody,
        publisherId,
        infiniteScrollExists
      });
      setValidationStatus(statusTxt);
      setTestedHtmlStr(testedHtml);
      if (statusTxt === 'FAIL' || statusTxt === 'Fetch failed') setErrorMessage(errorMsg);
      setWaitingForResponse(false);
    } catch (err) {
      setWaitingForResponse(false);
      setErrorMessage('Something went wrong');
    }
  };

  const showValidationPopup = () => {
    setValidationPopup(true);
  };
  const hideValidationPopup = () => {
    setValidationPopup(false);
  };

  const Buttons = () => (
    <div>
      {/* The validator is disabled for now and should be worked on later */}
      {/* <span className={styles.validateWrapper}>
        <Button disabled={waitingForResponse} type="secondary" onClick={validateHandler}>
          {waitingForResponse ? 'Please Wait...' : 'Validate'}
        </Button>
      </span> */}
      <Button type="primary" onClick={showValidationPopup}>
        Save
      </Button>
    </div>
  );

  const InfiniteScrollRadioButton = () => (
    <div>
      <Radio
        name="infinite-scroll-exists"
        selected={infiniteScrollExists ? 'true' : 'false'}
        onChange={infiniteScrollRadioBtnHandler}
      >
        <RadioOption value="true">Website uses infinite scroll</RadioOption>
        <RadioOption value="false">Website does not use infinite scroll</RadioOption>
      </Radio>
    </div>
  );

  const ValidatorUi = () => (
    <div className={styles.standardizedFont}>
      {validationStatus && (
        <>
          <hr className={styles.codeSeparator} />
          <div className={styles.validationStatusWrapper}>
            Validation Status:
            <span
              className={styles.validationStatus}
              style={{ backgroundColor: validationStatus === 'PASS' ? '#2fd072' : '#ff214b' }}
            >
              {validationStatus}
            </span>
          </div>
        </>
      )}
      <div>
        {(validationStatus === 'FAIL' || validationStatus === 'Fetch failed') && (
          <div className={styles.validationErrorsWrapper}>
            <div className={styles.validationErrorsLabel}>Error:</div>
            <textarea
              className={styles.validationErrors}
              style={{ height: '100px' }}
              readOnly={true}
              value={errorMessage}
            ></textarea>
          </div>
        )}
        {testedHtmlStr && (
          <div>
            <div className={styles.testedCodeLabel}>Tested HTML code (provided for your reference only):</div>
            <textarea
              className={styles.testedCode}
              style={{ height: '250px' }}
              readOnly={true}
              value={testedHtmlStr}
            ></textarea>
          </div>
        )}
      </div>
    </div>
  );

  function updateWidgetData(currentTab: number, key: string, value: boolean | String) {
    const data = key === 'code' ? encodeCustomCode(value as string) : value;
    dispatch(updateAmpWidgetSettings({ currentTab, key, value: data }));
    dispatch(updateHaveUnsavedChanges(true));
  }

  function insertWidgetCode(currentTab: number) {
    return (
      <div key={`widget-${currentTab}`}>
        <div className={styles.customCodeLabel}>Custom Code</div>
        <div className={styles.widgetText}>
          <TextArea
            value={checkValidEncodedCustomCode(widgets[Number(currentTab)].code)}
            onChange={(code: string) => updateWidgetData(Number(currentTab), 'code', code)}
            disabled={!hasEditAccess}
          />
        </div>
      </div>
    );
  }

  const getHeaderTitle = () => {
    if (currentTab === 'ampGenericStoryPage') return 'All Amp Story Pages';
    return `After Card ${Number(currentTab) + 1}`;
  };

  return (
    <div className={styles.tagsGeneralSettingsWrapper}>
      <div>
        <div className={styles.sidebarMainHeader}>Insert Code</div>
        <div
          onClick={() => setCurrentTab('ampGenericStoryPage')}
          className={`${styles.sidebarItem} ${currentTab === 'ampGenericStoryPage' ? styles.activeTab : ''}`}
        >
          All Amp Story Pages
        </div>
        <div className={styles.sidebarSeparator}></div>
        <div className={styles.sidebarSubHeader}>In-Story Widget Slots</div>
        <ul className={styles.widgetTabWrapper}>
          {widgets.map((w, i) => (
            <li
              className={`${styles.sidebarItem} ${Number(currentTab) === i ? styles.activeTab : ''}`}
              key={i + 1}
              onClick={() => setCurrentTab(i)}
            >
              After Card {i + 1}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className={styles.buttonsWrapper}>
          <div className={styles.mainHeader}>{getHeaderTitle()}</div>
          {hasEditAccess && <Buttons />}
        </div>
        <div className={styles.calloutWrapper}>
          <CustomCallout />
        </div>
        {currentTab === 'ampGenericStoryPage' ? (
          <>
            {insertCode('Insert to head', '<head>', insertToHead, 'insertToHead')}
            {insertCode('Insert to body', '<body>', insertToBody, 'insertToBody')}
          </>
        ) : (
          <>
            <div className={styles.checkboxWrapper}>
              <Checkbox
                id="enableWidgetCheckBox"
                checked={widgets[Number(currentTab)].enable}
                onChange={(checked: boolean) => updateWidgetData(Number(currentTab), 'enable', checked)}
              />
              <span
                className={styles.checkboxLabel}
                onClick={() => updateWidgetData(Number(currentTab), 'enable', !widgets[Number(currentTab)].enable)}
              >
                Enable Slot
              </span>
            </div>
            {insertWidgetCode(Number(currentTab))}
          </>
        )}

        {/* <InfiniteScrollRadioButton /> */}
        {!waitingForResponse && <ValidatorUi />}
      </div>
      <div className={styles.closeIcon} onClick={onClose}>
        <Close />
      </div>
      {showConfirmationPopup && (
        <ConfirmationPopup
          onSave={confirmationPopupSaveHandler}
          onCancel={onCancel}
          onDiscard={onDiscard}
          headingTxt="You have unsaved changes. Would you like to discard or save them?"
          subheadingTxt="Saving assumes your code is validated, and changes will apply after publishing."
        />
      )}
      {validationPopup && (
        <ConfirmationPopup
          onSave={saveHandler}
          onCancel={hideValidationPopup}
          onDiscard={hideValidationPopup}
          headingTxt="Warning: Confirm Code Validation"
          subheadingTxt="We assume your code has been validated. This action will save the code, and the changes will take effect upon publishing."
          discardBtnTxt="No, Go Back"
          saveBtnTxt="Yes, Code is Validated"
        />
      )}
      {showSaveMsg && <DisplayMessage />}
      {showErrMsg && (
        <DisplayMessage customMessage="Oops, server error countered! Changes made were not saved, please try again later!" />
      )}
    </div>
  );
};

export default AmpCustomCode;
